import type { SessionCoverageRow } from './queries/useTeamSessionCoverage';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';

/** ISO-week-ish bucket key — `YYYY-Wnn` so weeks sort lexicographically.
 *  Uses Monday as week start (ISO 8601). */
export function isoWeekKey(date: Date): string {
  // Clone to avoid mutating input.
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number
  // (Mon = 1 ... Sun = 7).
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

export interface WeekBucket {
  weekKey: string;
  /** Date of the Monday of this week (used for display). */
  weekStart: Date;
  sessionCount: number;
  totalMinutes: number;
  /** Coverage in minutes, NOT percent — caller derives percent against total. */
  minutesByPhase: Record<PhaseOfPlay, number>;
}

const PHASES: PhaseOfPlay[] = ['attacking', 'defending', 'transitions', 'individual'];

/** Aggregate sessions into ISO-week buckets with per-phase minute totals. */
export function bucketByWeek(rows: SessionCoverageRow[]): WeekBucket[] {
  const buckets = new Map<string, WeekBucket>();
  for (const r of rows) {
    if (!r.date) continue;
    const date = new Date(r.date + 'T00:00:00Z');
    if (Number.isNaN(date.getTime())) continue;
    const weekKey = isoWeekKey(date);
    let bucket = buckets.get(weekKey);
    if (!bucket) {
      // weekStart = the Monday on or before this session's date
      const start = new Date(date);
      const day = start.getUTCDay() || 7;
      start.setUTCDate(start.getUTCDate() - (day - 1));
      bucket = {
        weekKey,
        weekStart: start,
        sessionCount: 0,
        totalMinutes: 0,
        minutesByPhase: { attacking: 0, defending: 0, transitions: 0, individual: 0 },
      };
      buckets.set(weekKey, bucket);
    }
    bucket.sessionCount += 1;
    const sessionMins = r.duration_mins ?? 60;
    bucket.totalMinutes += sessionMins;
    if (r.phase_coverage) {
      for (const phase of PHASES) {
        const pct = r.phase_coverage[phase] ?? 0;
        bucket.minutesByPhase[phase] += (pct / 100) * sessionMins;
      }
    }
  }
  // Newest first — same axis as the page renders.
  return Array.from(buckets.values()).sort((a, b) => b.weekKey.localeCompare(a.weekKey));
}

/**
 * Rolling-28-day deficit check (§04.2) — returns the phases that fell
 * below the threshold AND a fingerprint of where things stand. Banner
 * UI fires when any phase is below 10% over the 28-day window. Empty
 * array when balance is healthy.
 */
export interface CoverageDeficit {
  /** Per-phase percentage over the lookback window. Sums roughly to 100. */
  pctByPhase: Record<PhaseOfPlay, number>;
  /** Phases below the threshold (strict <). */
  belowThreshold: PhaseOfPlay[];
  /** Sessions used in the calculation. */
  sessionCount: number;
}

export function detectDeficit(
  rows: SessionCoverageRow[],
  thresholdPct = 10,
  lookbackDays = 28,
): CoverageDeficit {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - lookbackDays);
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  const recent = rows.filter((r) => r.date >= cutoffIso);
  const minutesByPhase: Record<PhaseOfPlay, number> = {
    attacking: 0,
    defending: 0,
    transitions: 0,
    individual: 0,
  };
  let total = 0;
  for (const r of recent) {
    const mins = r.duration_mins ?? 60;
    total += mins;
    if (r.phase_coverage) {
      for (const phase of PHASES) {
        minutesByPhase[phase] += ((r.phase_coverage[phase] ?? 0) / 100) * mins;
      }
    }
  }
  const pctByPhase: Record<PhaseOfPlay, number> = total > 0
    ? {
        attacking: (minutesByPhase.attacking / total) * 100,
        defending: (minutesByPhase.defending / total) * 100,
        transitions: (minutesByPhase.transitions / total) * 100,
        individual: (minutesByPhase.individual / total) * 100,
      }
    : { attacking: 0, defending: 0, transitions: 0, individual: 0 };
  const belowThreshold = total > 0
    ? PHASES.filter((p) => pctByPhase[p] < thresholdPct)
    : [];
  return { pctByPhase, belowThreshold, sessionCount: recent.length };
}
